import { PullToRefresh, PullDownContent, ReleaseContent } from 'react-js-pull-to-refresh';
import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppContext } from '../../../Context/AppContext';
import { useAppDispatch, useAppSelector } from '../../../Hooks/Hooks';
import { RootState } from '../../../Redux/Store';
import { getSearchLoads, handleResetToggleFilter } from '../../../Redux/Reducers/SearchSlice';
import {
  dataBaseFriendlyDateFormat,
  getNewDates,
  scrollableDateFormat,
} from '../../../Utils/DateUtils';
import { isEmptyObject } from '../../../Utils/Basic';
import { ANYWHERE, PULL_TO_REFRESH } from '../../../Constants/Constants';
import ResultContainer from '../../../Components/ResultContainer/ResultContainer';
import './SearchResults.scss';

const SearchResults = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useContext(AppContext);
  const {
    searchQuery,
    searchResultData,
    searchSectionSelectedTab,
    sortAndFilter,
    selectedTopCardData,
  } = useAppSelector((state: RootState) => state?.search);
  const {
    favouriteSearchIndicator = false,
    userInteractionWithDestination = false,
    isStateSelectionEnabled = false,
  } = location.state || {};
  const showBanner = useAppSelector((state: RootState) => state.ContentOrConfig?.showBanner);

  const selectedTab = () => searchSectionSelectedTab?.find((item: any) => item?.selected)?.slug;

  useEffect(() => {
    if (!isMobile) navigate('/search');
  }, [isMobile, navigate]);

  const statesObject = Array.isArray(searchQuery?.destinationLocation?.State)
    ? searchQuery?.destinationLocation?.State.map((state: any) => state.code)
        .filter((state: any) => state !== undefined)
        .map((state: any) => state.slice(0, 2).toUpperCase())
    : searchQuery?.destinationLocation?.State;

  const states = searchQuery?.destinationLocation?.States;

  const getStates = () => {
    if (states) {
      return states;
    } else if (searchQuery?.destinationLocation?.State) {
      return statesObject;
    }
    return [];
  };
  const onRefresh = () => {
    dispatch(handleResetToggleFilter(true));
    return new Promise((resolve) => {
      const callback = () => {
        resolve(null);
      };
      let payload: any = {
        size: sortAndFilter?.size,
        page: 0,
        sortColumn: sortAndFilter?.sortColumn,
        sortDirection: sortAndFilter?.sortDirection,
        originCity: searchQuery?.originLocation?.City ?? ANYWHERE,
        originState: searchQuery?.originLocation?.State ?? '',
        originRadius: searchQuery?.originRadius,
        originCountry: searchQuery?.originLocation?.Country ?? 'US',
        originPostalCode: searchQuery?.originLocation?.Zip ?? '',
        destinationCity: !Array.isArray(searchQuery?.destinationLocation?.State)
          ? (searchQuery?.destinationLocation?.City ?? ANYWHERE)
          : '',
        destinationCountry: searchQuery?.destinationLocation?.Country ?? '',
        destinationPostalCode: searchQuery?.destinationLocation?.Zip ?? '',
        destinationRadius: searchQuery?.destinationRadius,
      };
      if ((searchQuery as any)?.id) {
        const pickUpDates = getNewDates(
          (searchQuery as any)?.pickupStartDate || '',
          (searchQuery as any)?.pickupEndDate || '',
          'origin',
        );
        const dropDates = getNewDates(
          (searchQuery as any)?.dropStartDate || '',
          (searchQuery as any)?.dropEndDate || '',
          'destination',
        );
        payload = {
          ...payload,
          favouriteSearchIndicator: (searchQuery as any)?.favouriteSearchIndicator,
          pickUpStartDate:
            dataBaseFriendlyDateFormat(
              pickUpDates?.newStartDate,
              'startDate',
              (searchQuery as any)?.timeZone || 'GMT',
            ) ?? '',
          pickUpEndDate:
            dataBaseFriendlyDateFormat(
              pickUpDates?.newEndDate,
              'endDate',
              (searchQuery as any)?.timeZone || 'GMT',
            ) ?? '',
          dropStartDate:
            dataBaseFriendlyDateFormat(
              dropDates?.newStartDate,
              'startDate',
              (searchQuery as any)?.timeZone || 'GMT',
            ) ?? '',
          dropEndDate:
            dataBaseFriendlyDateFormat(
              dropDates?.newEndDate,
              'endDate',
              (searchQuery as any)?.timeZone || 'GMT',
            ) ?? '',
          destinationStates: [
            (searchQuery as any)?.destinationState1,
            (searchQuery as any)?.destinationState2,
            (searchQuery as any)?.destinationState3,
          ]?.filter((item: any) => !!item),
        };
      } else {
        payload = {
          ...payload,
          favouriteSearchIndicator: favouriteSearchIndicator,
          pickUpStartDate:
            dataBaseFriendlyDateFormat(searchQuery?.originFromDate, 'startDate', 'GMT') ?? null,
          pickUpEndDate:
            dataBaseFriendlyDateFormat(searchQuery?.originToDate, 'endDate', 'GMT') ?? null,
          dropStartDate: userInteractionWithDestination
            ? dataBaseFriendlyDateFormat(searchQuery?.destinationFromDate, 'startDate', 'GMT') || ''
            : '',
          dropEndDate: userInteractionWithDestination
            ? dataBaseFriendlyDateFormat(searchQuery?.destinationToDate, 'endDate', 'GMT') || ''
            : '',

          destinationStates: Array.isArray(searchQuery?.destinationLocation?.State)
            ? searchQuery?.destinationLocation?.State?.filter(
                (item?: any) => !isEmptyObject(item),
              ).map((item: any) => item?.code)
            : searchQuery?.destinationLocation?.State
              ? [searchQuery?.destinationLocation?.State]
              : [],
        };
      }
      dispatch(getSearchLoads({ queryPayloads: payload, hideLoader: true, callback }));
    });
  };

  return (
    <div className={`Search_Results_Container${showBanner ? ' AlertBannerVisible' : ''}`}>
      {searchQuery && (
        <div data-testid="ScrollableContainer" className="Scrollable_Container">
          {searchQuery.originLocation?.City ? (
            <span>
              Origin: {searchQuery.originLocation.City}, {searchQuery.originLocation.State}
            </span>
          ) : (
            <span>Origin: Anywhere</span>
          )}
          <span>Origin Radius: {searchQuery.originRadius} mi</span>
          <span>
            Pick-up Date: {scrollableDateFormat(searchQuery.originFromDate)} -{' '}
            {scrollableDateFormat(searchQuery.originToDate)}
          </span>
          {searchQuery.destinationLocation?.City ? (
            <span>
              Destination: {searchQuery.destinationLocation.City}
              {searchQuery.destinationLocation.State
                ? ` ${searchQuery.destinationLocation.State}`
                : ''}
            </span>
          ) : states || statesObject ? (
            <span>
              Destination:
              {getStates()?.map((state: any, index: any) => (
                <span className="State_Arr" key={state}>
                  {' '}
                  {state}
                  {index !== getStates().length - 1 && ','}
                </span>
              ))}
            </span>
          ) : (
            <span> Destination: Anywhere</span>
          )}
          <span>
            Drop Date: {scrollableDateFormat(searchQuery.destinationFromDate)} -{' '}
            {scrollableDateFormat(searchQuery.destinationToDate)}
          </span>
          {searchQuery?.minRPM ? (
            <span>
              Min Loaded RPM :{' '}
              {searchQuery?.minRPM === null || searchQuery?.minRPM === 'null'
                ? 'No Min'
                : `$${parseFloat(searchQuery?.minRPM).toFixed(2)}`}
            </span>
          ) : null}
          {searchQuery?.minMiles || selectedTopCardData?.minMiles ? (
            <span>
              Load Distance Min : {searchQuery?.minMiles || selectedTopCardData?.minMiles}
            </span>
          ) : null}
          {searchQuery?.maxMiles || selectedTopCardData?.maxMiles ? (
            <span>
              Load Distance Max :{' '}
              {(searchQuery?.maxMiles || selectedTopCardData?.maxMiles) === 9999
                ? '1000+'
                : searchQuery?.maxMiles || selectedTopCardData?.maxMiles}
            </span>
          ) : null}
        </div>
      )}
      <PullToRefresh
        pullDownContent={<PullDownContent label="" />}
        releaseContent={<ReleaseContent label="" />}
        refreshContent={
          <div>
            <img
              src="/oo/assets/images/Schneider_Spinner-transparent.gif"
              alt=""
              className="PullToRefresh-Loader"
            />
          </div>
        }
        pullDownThreshold={PULL_TO_REFRESH.THRESHOLD}
        onRefresh={onRefresh}
        triggerHeight={PULL_TO_REFRESH.TRIGGER_HEIGHT}
        backgroundColor="#e8e5e1"
        startInvisible={true}
      >
        <div className="Cards_Container">
          <ResultContainer
            additionalClass="Result_Section"
            label="Results"
            totalRecords={searchResultData?.totalRecords}
            loadPostings={searchResultData?.loadPostings}
            isFavorite={selectedTab() === 'FavoriteSearch'}
            isRecent={selectedTab() === 'RecentSearch'}
            isNewSearch={selectedTab() === 'NewSearch'}
            isStateSelectionEnabled={isStateSelectionEnabled}
          />
        </div>
      </PullToRefresh>
    </div>
  );
};

export default SearchResults;
